/**
 * Business Partners View
 *
 * Page for managing business partners (Service Providers and Portfolio Companies).
 */

import { useEffect, useState, useMemo, useCallback } from "react";
import { buildRowCount } from "@/useCases/rowCount";
import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { Card } from "@/ui/atoms/Card";
import { Loader, Modal, DialogFooter, Button } from "@/ui/atoms";
import { PageLayout } from "@/ui/layouts";
import { DataTable, EmptyState, SearchableCheckboxList } from "@/ui/molecules";
import { useServerTable, type TServerTableQueryInput } from "@/useCases/useServerTable";
import {
    booleanParam,
    multiSelectParam,
    selectParam,
    sortParam,
} from "@/useCases/serverTableQuery";
import { STRING_MULTI_SELECT_BLANK } from "@/utils/tableMultiSelectFilter";
import {
    Building2,
    Plus,
    X,
    FileText,
    Upload,
    AlertCircle,
    CheckCircle,
    Download,
} from "lucide-react";
import { cn } from "@/utils/utils";
import { exportCoaErrorsToExcel } from "@/utils/exportCoaErrors";
import { routerPaths } from "@/ui/router";
import { createServiceProviderColumns } from "./serviceProviderColumns";
import type { TAsyncState } from "@/store";
import type { TBusinessPartnerType, TDeal } from "@/store";
import { ApiError, messageFromProdaErrorBody } from "@/apis/proda_finance";
import type {
    TApiDealFileType,
    TApiGlType,
    TApiFinalFileDestination,
    TApiPropcodeMappingValidationResponse,
    TApiSegmentMappingValidationResponse,
    TApiSegment2MappingBehavior,
    TApiSegment4MappingBehavior,
    TApiSegment5MappingBehavior,
    TApiSegment6MappingBehavior,
    TApiSegment7MappingBehavior,
    TApiColumnExtractionResponse,
    TApiColumnMappingFileType,
    TApiColumnMappingFieldType,
    TApiOTBUploadResponse,
} from "@/apis/proda_finance";

/**
 * CoA Level type
 */
export type TCoALevel = "A" | "P" | "C";

/**
 * CoA Mapping view type
 */
export type TCoAMappingView = {
    id: string;
    thirdAccountCode: string;
    thirdAccountName: string;
    yardiAccountId: string;
    yardiAccountCode: string | null;
    yardiAccountName: string | null;
    level: TCoALevel;
    isInForce: boolean;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

/**
 * CoA Upload result type
 */
export type TCoAUploadResult = {
    success: boolean;
    errors: string[];
    warnings: string[];
    mappingsCreated: number;
    mappingsDeleted: number;
    storagePath: string | null;
};

/**
 * Deal Segment Mapping view type
 */
export type TDealSegmentMappingView = {
    id: string;
    segmentType: number;
    sourceSegmentValue: string;
    sourceSegmentName: string | null;
    targetSegmentValue: string;
    targetSegmentName: string | null;
    isActive: boolean;
};

/**
 * Deal Propcode Mapping view type
 */
export type TDealPropcodeMappingView = {
    id: string;
    level: string;
    sourcePropertyCode: string;
    sourcePropertyName: string | null;
    targetPropertyCode: string;
    targetPropertyName: string | null;
    isActive: boolean;
};

/**
 * Service Provider type for the view
 */
export type TEmployeeInfo = {
    id: string;
    display_name: string;
    email: string;
};

/** A business partner's deal counts, rendered on the Deals button as active/all. */
export type TDealCountsSummary = {
    active: number;
    total: number;
};

export type TServiceProviderView = {
    id: string;
    companyId: string;
    name: string;
    code: string;
    businessPartnerType: TBusinessPartnerType;
    contactEmail: string | null;
    contactName: string | null;
    contactPhone: string | null;
    defaultDateFormat: string;
    defaultPeriodFormat: string;
    currencyId: string | null;
    canUploadSupportingFiles: boolean;
    isActive: boolean;
    isTest: boolean;
    employees: TEmployeeInfo[];
    notes: string | null;
    createdAt: string;
    updatedAt: string;
};

export type TPaginatedServiceProvidersView = {
    items: TServiceProviderView[];
    total: number;
    page: number;
    pageSize: number;
};

export type TViewServiceProvidersParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useServiceProvidersState: () => TAsyncState<TPaginatedServiceProvidersView>;
        /** Environment flag, not a permission — the feature is absent when off. */
        multiCriteriaCoaAvailable: boolean;
        onCreateServiceProvider: (data: {
            name: string;
            businessPartnerType?: TBusinessPartnerType;
            contactEmail?: string | null;
            contactName?: string | null;
            contactPhone?: string | null;
            defaultDateFormat?: string;
            defaultPeriodFormat?: string;
            currencyId?: string | null;
            canUploadSupportingFiles?: boolean;
            coaMappingMode?: "single" | "multi_criteria";
            isTest?: boolean;
            employeeIds?: string[];
        }) => Promise<TServiceProviderView>;
        onUpdateServiceProvider: (
            id: string,
            data: {
                name?: string;
                contactEmail?: string | null;
                contactName?: string | null;
                contactPhone?: string | null;
                defaultDateFormat?: string;
                defaultPeriodFormat?: string;
                canUploadSupportingFiles?: boolean;
                isActive?: boolean;
                isTest?: boolean;
                notes?: string | null;
                employeeIds?: string[];
            },
            businessPartnerType?: "service_provider" | "portfolio_company",
        ) => Promise<void>;
        onDeleteServiceProvider: (
            id: string,
            businessPartnerType?: "service_provider" | "portfolio_company",
        ) => Promise<void>;
        onCreateBusinessPartnerWithCoa: (
            data: {
                name: string;
                businessPartnerType?: TBusinessPartnerType;
                contactEmail?: string | null;
                contactName?: string | null;
                contactPhone?: string | null;
                defaultDateFormat?: string;
                defaultPeriodFormat?: string;
                currencyId?: string | null;
                canUploadSupportingFiles?: boolean;
                coaMappingMode?: "single" | "multi_criteria";
                isTest?: boolean;
                employeeIds?: string[];
            },
            file: File,
            businessPartnerType: "service_provider" | "portfolio_company",
        ) => Promise<TServiceProviderView>;
        onRefresh: () => void;
        // CoA Upload (ownerId is service provider id or portfolio company id depending on type)
        onUploadCoA: (
            ownerId: string,
            file: File,
            businessPartnerType: "service_provider" | "portfolio_company",
        ) => Promise<TCoAUploadResult>;
        onLoadCoAMappings: (
            ownerId: string,
            businessPartnerType: "service_provider" | "portfolio_company",
        ) => Promise<TCoAMappingView[]>;
        // Deals
        useDealCounts: () => Record<string, TDealCountsSummary>;
        onLoadDeals: (
            bpId: string,
            businessPartnerType: "service_provider" | "portfolio_company",
        ) => Promise<TDeal[]>;
        onLoadDealSegmentMappings: (dealId: string) => Promise<TDealSegmentMappingView[]>;
        onLoadDealPropcodeMappings: (dealId: string) => Promise<TDealPropcodeMappingView[]>;
        // Deal creation
        useServiceProvidersForDeals: () => {
            data: Array<{ id: string; name: string; code: string }> | null;
            loading: boolean;
        };
        usePortfolioCompanies: () => {
            data: Array<{ id: string; name: string; code: string }> | null;
            loading: boolean;
        };
        useUsers: () => {
            data: Array<{ id: string; displayName: string; email: string }> | null;
            loading: boolean;
        };
        onCreateDeal: (data: {
            name: string;
            code: string;
            serviceProviderId: string;
            portfolioCompanyId: string;
            financeUserIds: string[];
            dealFileType: TApiDealFileType;
            glType: TApiGlType;
            finalFileDestination: TApiFinalFileDestination;
            monthlyDeadlineDay: number;
            startPeriod: string;
            segment2MappingType: TApiSegment2MappingBehavior;
            segment4MappingType: TApiSegment4MappingBehavior;
            segment5MappingType: TApiSegment5MappingBehavior;
            segment6MappingType: TApiSegment6MappingBehavior;
            segment7MappingType: TApiSegment7MappingBehavior;
            segmentMappings: Record<
                number,
                Array<{
                    source_counterparty_code: string;
                    source_counterparty_name: string;
                    target_value: string;
                }>
            >;
            propcodeMappings: Array<{
                level: string;
                source_propcode: string;
                target_propcode: string;
            }>;
            columnMappings: Array<{
                file_type: TApiColumnMappingFileType;
                field_type: TApiColumnMappingFieldType;
                column_name: string;
                header_row_number: number;
            }>;
            acceptsOtb?: boolean;
        }) => Promise<{ id: string } | void>;
        onValidateSegmentMappingFile: (
            file: File,
            segmentNumber: number,
        ) => Promise<TApiSegmentMappingValidationResponse>;
        onValidatePropcodeMappingFile: (
            file: File,
        ) => Promise<TApiPropcodeMappingValidationResponse>;
        onUploadOTB: (
            dealId: string,
            file: File,
            options?: { sheetName?: string; dateFormat?: string },
        ) => Promise<TApiOTBUploadResponse>;
        onGeneratePlaceholderOTB: (
            dealId: string,
            propertyCode: string,
        ) => Promise<TApiOTBUploadResponse>;
        onDeleteDeal: (dealId: string) => Promise<void>;
        onExtractColumns: (
            file: File,
            headerRowNumber: number,
        ) => Promise<TApiColumnExtractionResponse>;
        /** Server-mode BP loader: called by useServerTable on every filter/sort/page change. */
        loadServiceProviders: (
            query: {
                skip: number;
                limit: number;
                name?: string;
                business_partner_ids?: string[];
                employee_ids?: string[];
                employees_unassigned?: boolean;
                business_partner_type?: string;
                can_upload_supporting_files?: boolean;
                is_active?: boolean;
                sort_by?: string;
                sort_dir?: string;
            },
            signal?: AbortSignal,
        ) => void;
        useTotalUnfiltered: () => number;
        /** Options for the Name column's multiSelect filter (value = BP id). */
        useBusinessPartnerOptions: () => { value: string; label: string }[];
        /** Options for the Employees column's multiSelect filter (value = user id). */
        useEmployeeOptions: () => { value: string; label: string }[];
        canCreate: boolean;
        canCreateDeal: boolean;
        canViewDeals: boolean;
    };
};

const PAGE_SIZE = 50;

export type TViewServiceProvidersProps = Record<string, never>;

const businessPartnerTypeLabels: Record<TBusinessPartnerType, string> = {
    service_provider: "Service Provider",
    portfolio_company: "Portfolio Company",
};

const businessPartnerTypes: { value: TBusinessPartnerType; label: string }[] = [
    { value: "service_provider", label: "Service Provider" },
    { value: "portfolio_company", label: "Portfolio Company" },
];

// ViewServiceProviders uses a stricter `headerRowNumber: number` (no empty-string sentinel).
// Local copy preserves that; modal will be replaced by <DealWizard> in a later step.

export const createViewServiceProviders = ({ useCases }: TViewServiceProvidersParams) => {
    return function ViewServiceProviders() {
        const {
            onMount,
            onUnmount,
            useServiceProvidersState,
            onCreateBusinessPartnerWithCoa,
            onRefresh,
            onLoadCoAMappings,
            useDealCounts,
            useUsers,
            loadServiceProviders,
            useTotalUnfiltered,
            useBusinessPartnerOptions,
            useEmployeeOptions,
            canCreate,
            canCreateDeal,
            canViewDeals,
            multiCriteriaCoaAvailable,
        } = useCases();

        const serviceProvidersState = useServiceProvidersState();
        const totalUnfiltered = useTotalUnfiltered();
        const businessPartnerOptions = useBusinessPartnerOptions();
        const employeeOptions = useEmployeeOptions();
        const dealCounts = useDealCounts();
        const users = useUsers();

        const spToQuery = useCallback((input: TServerTableQueryInput) => {
            // The Employees column ticks user ids plus, optionally, the shared
            // "(Blanks)" sentinel — which is not an id, so it travels as its own
            // boolean param rather than polluting employee_ids.
            const employeeFilter = multiSelectParam(input.columnFilters, "employees");
            const employeeIds = employeeFilter?.filter((v) => v !== STRING_MULTI_SELECT_BLANK);
            const wantsUnassigned = employeeFilter?.includes(STRING_MULTI_SELECT_BLANK);
            return {
                skip: (input.page - 1) * PAGE_SIZE,
                limit: PAGE_SIZE,
                // The Name column is a multiSelect over existing BP names; the
                // ticked option values are BP ids, sent as business_partner_ids.
                business_partner_ids: multiSelectParam(input.columnFilters, "name"),
                employee_ids: employeeIds?.length ? employeeIds : undefined,
                employees_unassigned: wantsUnassigned ? true : undefined,
                business_partner_type: selectParam(input.columnFilters, "businessPartnerType"),
                can_upload_supporting_files: booleanParam(
                    input.columnFilters,
                    "canUploadSupportingFiles",
                ),
                is_active: booleanParam(input.columnFilters, "isActive"),
                ...sortParam(input.sorting),
            };
        }, []);
        const server = useServerTable({
            pageSize: PAGE_SIZE,
            toQuery: spToQuery,
            fetchData: loadServiceProviders,
        });

        const [isModalOpen, setIsModalOpen] = useState(false);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [error, setError] = useState<string | null>(null);

        // Form state
        const [formData, setFormData] = useState({
            name: "",
            businessPartnerType: "service_provider" as TBusinessPartnerType,
            canUploadSupportingFiles: false,
            multiCriteriaCoa: false,
            isTest: false,
            isActive: false,
            employeeIds: [] as string[],
            currencyId: "",
            notes: "",
        });

        // CoA Upload state
        const [coaFile, setCoaFile] = useState<File | null>(null);
        const [coaUploadResult, setCoaUploadResult] = useState<TCoAUploadResult | null>(null);
        const [coaMappings, setCoaMappings] = useState<TCoAMappingView[]>([]);
        const [isUploadingCoA, setIsUploadingCoA] = useState(false);
        const isLoadingCoA = false;
        const [coaUploadError, setCoaUploadError] = useState<string | null>(null);
        // Track if we're in the "BP created, awaiting CoA" state for new partners
        const [createdServiceProviderId, setCreatedServiceProviderId] = useState<string | null>(
            null,
        );
        // Track if creation was successful (to show success state instead of auto-closing)
        const [creationSuccess, setCreationSuccess] = useState(false);

        // Navigation
        const navigate = useNavigate();

        // Create Deal Modal state
        // Steps: 1=Initial Info, 2=Column Mapping, 3=Segment Mapping, 4=Propcode Mapping, 5=OTB Upload
        // OTB Upload state (Step 5)

        // Column mapping state (Step 2)

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const resetForm = () => {
            setFormData({
                name: "",
                businessPartnerType: "service_provider",
                canUploadSupportingFiles: false,
                multiCriteriaCoa: false,
                isTest: false,
                isActive: false,
                employeeIds: [],
                currencyId: "",
                notes: "",
            });
            setError(null);
            // Reset CoA state
            setCoaFile(null);
            setCoaUploadResult(null);
            setCoaMappings([]);
            setCoaUploadError(null);
            setCreatedServiceProviderId(null);
            setCreationSuccess(false);
        };

        const openCreateModal = () => {
            resetForm();
            setIsModalOpen(true);
        };

        const closeModal = () => {
            // If we created a service provider but CoA upload failed, we need to clean up
            if (createdServiceProviderId && !coaUploadResult?.success) {
                // Don't close, let user retry or handle it
                return;
            }
            setIsModalOpen(false);
            resetForm();
        };

        // Navigate to deal create page (SP → business-partners route, PC → portfolio-companies route so ViewDealCreate gets pcId)
        const navigateToDealCreate = useCallback(
            (bp: TServiceProviderView) => {
                if (bp.businessPartnerType === "portfolio_company") {
                    navigate(routerPaths.admin.portfolioCompanyDealCreate(bp.id));
                } else {
                    navigate(routerPaths.admin.businessPartnerDealCreate(bp.id));
                }
            },
            [navigate],
        );

        // Check if segment needs mapping (not "no_mapping" and not "use_from_file")

        // Get segments that need mapping files

        // Check if all required segment mappings have valid uploads

        // Handle segment file selection

        // Check if segment is enabled (mapping type != no_mapping)

        // Get required column fields based on file type and enabled segments

        // Check if column mapping is complete

        // Check if all column mappings are valid for Step 2

        // Handle column file selection

        // Handle clearing column file

        // Handle column mapping change

        // Handle file selection for CoA upload
        const handleCoAFileSelect = (file: File) => {
            if (!file.name.endsWith(".xlsx")) {
                setCoaUploadError("Please select an Excel file (.xlsx)");
                return;
            }
            setCoaFile(file);
            setCoaUploadError(null);
            setCoaUploadResult(null);
        };

        const handleSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setError(null);
            setIsSubmitting(true);

            try {
                // Create new business partner
                // CoA file is required for both service providers and portfolio companies
                if (!coaFile) {
                    setError("CoA mapping file file is required");
                    setIsSubmitting(false);
                    return;
                }
                if (formData.employeeIds.length === 0) {
                    setError("Select at least one employee for this business partner.");
                    setIsSubmitting(false);
                    return;
                }

                const duplicateCheckName = formData.name.trim();
                const duplicateCheckNorm = duplicateCheckName.toLowerCase();
                const duplicateCheckItems = serviceProvidersState.data?.items;
                if (
                    duplicateCheckNorm &&
                    duplicateCheckItems !== undefined &&
                    duplicateCheckItems.length > 0
                ) {
                    const nameTaken = duplicateCheckItems.some(
                        (bp) => bp.name.trim().toLowerCase() === duplicateCheckNorm,
                    );
                    if (nameTaken) {
                        setError(
                            `A business partner named '${duplicateCheckName}' already exists for this company.`,
                        );
                        setIsSubmitting(false);
                        return;
                    }
                }

                const bpData = {
                    name: formData.name,
                    businessPartnerType: formData.businessPartnerType,
                    contactEmail: null as string | null,
                    contactName: null as string | null,
                    contactPhone: null as string | null,
                    defaultDateFormat: "YYYY-MM-DD",
                    defaultPeriodFormat: "YYYY-MM",
                    canUploadSupportingFiles: formData.canUploadSupportingFiles,
                    coaMappingMode: (formData.multiCriteriaCoa ? "multi_criteria" : "single") as
                        | "single"
                        | "multi_criteria",
                    isTest: formData.isTest,
                    currencyId: formData.currencyId || null,
                    employeeIds: formData.employeeIds,
                };

                // Atomic create + CoA upload in one request — server rolls back if CoA fails
                const created = await onCreateBusinessPartnerWithCoa(
                    bpData,
                    coaFile,
                    formData.businessPartnerType,
                );

                setCreatedServiceProviderId(created.id);

                // Load CoA mappings to show in the success panel
                setIsUploadingCoA(true);
                try {
                    const mappings = await onLoadCoAMappings(
                        created.id,
                        formData.businessPartnerType,
                    );
                    setCoaMappings(mappings);
                    setCoaUploadResult({
                        success: true,
                        errors: [],
                        warnings: [],
                        mappingsCreated: mappings.length,
                        mappingsDeleted: 0,
                        storagePath: null,
                    });
                } catch {
                    // Non-fatal — success path continues regardless
                } finally {
                    setIsUploadingCoA(false);
                }

                onRefresh();
                // Success — show success state, don't close modal yet
                setCreationSuccess(true);
            } catch (err) {
                console.error("Business partner creation error:", err);
                if (err instanceof ApiError) {
                    // Surface CoA-specific errors from the atomic endpoint
                    const body = err.response as
                        | { error?: { details?: { errors?: string[] } } }
                        | undefined;
                    const coaErrors = body?.error?.details?.errors;
                    if (Array.isArray(coaErrors) && coaErrors.length > 0) {
                        setCoaUploadResult({
                            success: false,
                            errors: coaErrors,
                            warnings: [],
                            mappingsCreated: 0,
                            mappingsDeleted: 0,
                            storagePath: null,
                        });
                        setCoaUploadError("CoA upload failed");
                    } else {
                        setError(messageFromProdaErrorBody(err.response, err.status));
                    }
                } else {
                    setError(
                        err instanceof Error ? err.message : "Failed to save business partner",
                    );
                }
            } finally {
                setIsSubmitting(false);
            }
        };

        // Define table columns
        const columns = useMemo<ColumnDef<TServiceProviderView>[]>(
            () =>
                createServiceProviderColumns(
                    navigateToDealCreate,
                    navigate,
                    dealCounts,
                    businessPartnerTypeLabels,
                    businessPartnerTypes,
                    canCreateDeal,
                    canViewDeals,
                    businessPartnerOptions,
                    employeeOptions,
                ),
            [
                navigateToDealCreate,
                navigate,
                dealCounts,
                canCreateDeal,
                canViewDeals,
                businessPartnerOptions,
                employeeOptions,
            ],
        );

        const renderContent = () => {
            if (serviceProvidersState.status === "pending" && !serviceProvidersState.data) {
                return (
                    <div className="flex justify-center py-12">
                        <Loader size="lg" />
                    </div>
                );
            }

            if (serviceProvidersState.status === "rejected") {
                return (
                    <EmptyState
                        variant="error"
                        title="Failed to load business partners"
                        description={serviceProvidersState.error?.message || "An error occurred"}
                        action={{ label: "Try Again", onClick: onRefresh }}
                    />
                );
            }

            const total = serviceProvidersState.data?.total ?? 0;

            return (
                <DataTable
                    columns={columns}
                    storageKey="business-partners"
                    data={serviceProvidersState.data?.items ?? []}
                    loading={serviceProvidersState.status === "pending"}
                    emptyMessage="No business partners found."
                    manualFiltering
                    manualSorting
                    sortingState={server.sorting}
                    onSortingChange={server.onSortingChange}
                    initialColumnFilters={server.columnFilters}
                    onColumnFiltersChange={server.onColumnFiltersChange}
                    rowNoun={{ one: "business partner", many: "business partners" }}
                    rowCount={buildRowCount({
                        total: total,
                        totalUnfiltered: totalUnfiltered,
                        hasActiveFilters: server.columnFilters.length > 0,
                    })}
                    pagination={{
                        page: server.page,
                        pageSize: PAGE_SIZE,
                        total,
                        onPageChange: server.setPage,
                    }}
                />
            );
        };

        return (
            <>
                <PageLayout
                    title="Business Partners"
                    description="Manage Service Providers and Portfolio Companies"
                    actions={
                        canCreate ? (
                            <Button onClick={openCreateModal}>
                                <Plus className="mr-2 h-4 w-4" />
                                Add Business Partner
                            </Button>
                        ) : undefined
                    }
                >
                    <Card className="overflow-hidden">{renderContent()}</Card>
                </PageLayout>

                {/* Create Modal */}
                <Modal
                    open={isModalOpen}
                    onOpenChange={(open) => {
                        if (!open) closeModal();
                    }}
                    title={creationSuccess ? "Business Partner Created" : "Add Business Partner"}
                    description={
                        creationSuccess
                            ? formData.businessPartnerType === "portfolio_company"
                                ? "Your portfolio company has been created successfully."
                                : "Your business partner has been created successfully with the uploaded Chart of Accounts mappings."
                            : "Fill in the details to add a new business partner."
                    }
                    className="max-w-2xl max-h-[90vh] flex flex-col"
                >
                    <form onSubmit={handleSubmit} className="space-y-5 overflow-y-auto flex-1 pr-2">
                        {/* Success banner */}
                        {creationSuccess && (
                            <div className="rounded-lg bg-emerald-500/10 p-4 text-emerald-700 border border-emerald-200">
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-medium">
                                            Business Partner Created Successfully!
                                        </p>
                                        <p className="text-sm mt-1">
                                            <strong>{formData.name}</strong> has been created
                                            {formData.businessPartnerType === "service_provider" &&
                                            coaUploadResult
                                                ? ` with ${coaUploadResult.mappingsCreated || 0} CoA mappings.`
                                                : "."}
                                        </p>
                                        <p className="text-sm text-emerald-600 mt-2">
                                            Review the uploaded mappings below, then click
                                            &quot;Done&quot; to close.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                                {error}
                            </div>
                        )}

                        {/* Basic Information */}
                        <div className="space-y-3">
                            <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                                <Building2 className="h-4 w-4" />
                                Basic Information
                            </h4>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        Business Partner Type{" "}
                                        <span className="text-destructive">*</span>
                                    </label>
                                    <select
                                        value={formData.businessPartnerType}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                businessPartnerType: e.target
                                                    .value as TBusinessPartnerType,
                                            })
                                        }
                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                                        required
                                    >
                                        {businessPartnerTypes.map((type) => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        Name <span className="text-destructive">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) =>
                                            setFormData({ ...formData, name: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-60"
                                        placeholder="Acme Property Management"
                                        required
                                        autoFocus
                                        disabled={creationSuccess}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Options */}
                        <div className="space-y-3 pt-2 border-t">
                            <div className="grid grid-cols-3 gap-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.isActive}
                                        onChange={(e) => {
                                            const active = e.target.checked;
                                            setFormData({
                                                ...formData,
                                                isActive: active,
                                                ...(active
                                                    ? { canUploadSupportingFiles: true }
                                                    : {}),
                                            });
                                        }}
                                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                    <span className="text-sm">Active</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.isTest}
                                        onChange={(e) =>
                                            setFormData({ ...formData, isTest: e.target.checked })
                                        }
                                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                    <span className="text-sm">Test user</span>
                                </label>
                                {!formData.isActive && (
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.canUploadSupportingFiles}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    canUploadSupportingFiles: e.target.checked,
                                                })
                                            }
                                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm">Upload Supporting Files</span>
                                    </label>
                                )}
                                {/* Hidden where the environment does not have
                                    multi-criteria CoA mapping. Set at creation
                                    because a partner created together with a
                                    multi-criteria CoA file is rejected unless
                                    the mode is already on. */}
                                {multiCriteriaCoaAvailable && (
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.multiCriteriaCoa}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    multiCriteriaCoa: e.target.checked,
                                                })
                                            }
                                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm">Multi-criteria CoA mapping</span>
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* Employees (Required) */}
                        <div className="space-y-3 pt-2 border-t">
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Employees <span className="text-destructive">*</span>
                                </label>
                                <SearchableCheckboxList
                                    options={(users.data ?? []).map((user) => ({
                                        id: user.id,
                                        label: user.displayName,
                                        sublabel: user.email,
                                    }))}
                                    selectedIds={formData.employeeIds}
                                    onChange={(employeeIds) =>
                                        setFormData({ ...formData, employeeIds })
                                    }
                                    disabled={creationSuccess}
                                    loading={users.loading}
                                    searchPlaceholder="Search employees..."
                                    emptyMessage="No users available"
                                    loadingMessage="Loading users..."
                                />
                                {formData.employeeIds.length > 0 && (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        {formData.employeeIds.length} employee
                                        {formData.employeeIds.length !== 1 ? "s" : ""} selected
                                    </p>
                                )}
                                {formData.employeeIds.length === 0 && !creationSuccess && (
                                    <p className="mt-1 text-xs text-destructive">
                                        At least one employee is required
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Currency (Portfolio Company only) */}
                        {formData.businessPartnerType === "portfolio_company" && (
                            <div className="space-y-3 pt-2 border-t">
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        Currency
                                    </label>
                                    <select
                                        value={formData.currencyId}
                                        onChange={(e) =>
                                            setFormData({ ...formData, currencyId: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                                        disabled={creationSuccess}
                                    >
                                        <option value="">Select currency (optional)</option>
                                        <option value="AUD">AUD</option>
                                        <option value="BRL">BRL</option>
                                        <option value="CAD">CAD</option>
                                        <option value="CHF">CHF</option>
                                        <option value="CNY">CNY</option>
                                        <option value="DKK">DKK</option>
                                        <option value="EUR">EUR</option>
                                        <option value="GBP">GBP</option>
                                        <option value="HKD">HKD</option>
                                        <option value="INR">INR</option>
                                        <option value="JPY">JPY</option>
                                        <option value="KRW">KRW</option>
                                        <option value="MXN">MXN</option>
                                        <option value="NOK">NOK</option>
                                        <option value="NZD">NZD</option>
                                        <option value="RUB">RUB</option>
                                        <option value="SEK">SEK</option>
                                        <option value="SGD">SGD</option>
                                        <option value="TRY">TRY</option>
                                        <option value="USD">USD</option>
                                        <option value="ZAR">ZAR</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* Chart of Accounts Upload - required for both types */}
                        <div className="space-y-3 pt-2 border-t">
                            <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                                <Upload className="h-4 w-4" />
                                Upload CoA mapping file
                                {!creationSuccess && <span className="text-destructive">*</span>}
                            </h4>

                            {/* File drop zone - hide when creation is successful */}
                            {!creationSuccess && (
                                <label
                                    className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer block ${
                                        coaFile
                                            ? "border-primary bg-primary/5"
                                            : "border-muted-foreground/25 hover:border-primary/50 hover:bg-primary/5"
                                    }`}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const file = e.dataTransfer.files[0];
                                        if (file) handleCoAFileSelect(file);
                                    }}
                                >
                                    <input
                                        type="file"
                                        accept=".xlsx"
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleCoAFileSelect(file);
                                        }}
                                    />
                                    {coaFile ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <FileText className="h-5 w-5 text-primary" />
                                            <span className="text-sm font-medium">
                                                {coaFile.name}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setCoaFile(null);
                                                    setCoaUploadResult(null);
                                                    setCoaUploadError(null);
                                                }}
                                                className="ml-2 text-muted-foreground hover:text-foreground"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div>
                                            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                                            <p className="text-sm text-muted-foreground mb-1">
                                                Drag and drop your Excel file here, or{" "}
                                                <span className="text-primary hover:underline">
                                                    click to browse
                                                </span>
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-2">
                                                Required columns: Third Account Code, Third Account
                                                Name, Platform Account Name, Platform Account Code,
                                                Level
                                            </p>
                                        </div>
                                    )}
                                </label>
                            )}

                            {/* CoA upload error */}
                            {coaUploadError && (
                                <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm flex items-start gap-2">
                                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                    <span>{coaUploadError}</span>
                                </div>
                            )}

                            {/* CoA upload result */}
                            {coaUploadResult && (
                                <div
                                    className={`rounded-lg p-3 text-sm ${
                                        coaUploadResult.success
                                            ? "bg-emerald-500/10 text-emerald-700"
                                            : "bg-destructive/10 text-destructive"
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        {coaUploadResult.success ? (
                                            <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                        ) : (
                                            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                        )}
                                        <div className="flex-1">
                                            <p className="font-medium">
                                                {coaUploadResult.success
                                                    ? `Successfully uploaded ${coaUploadResult.mappingsCreated} mappings`
                                                    : "Upload failed"}
                                            </p>
                                            {coaUploadResult.errors.length > 0 && (
                                                <ul className="mt-1 space-y-0.5 list-disc list-inside">
                                                    {coaUploadResult.errors
                                                        .slice(0, 5)
                                                        .map((err, i) => (
                                                            <li key={i} className="text-xs">
                                                                {err}
                                                            </li>
                                                        ))}
                                                    {coaUploadResult.errors.length > 5 && (
                                                        <li className="text-xs">
                                                            ... and{" "}
                                                            {coaUploadResult.errors.length - 5} more
                                                            errors
                                                        </li>
                                                    )}
                                                </ul>
                                            )}
                                            {!coaUploadResult.success &&
                                                coaUploadResult.errors.length > 0 && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="mt-2"
                                                        onClick={() =>
                                                            exportCoaErrorsToExcel(
                                                                coaFile,
                                                                coaUploadResult.errors,
                                                                `CoA mapping errors - ${
                                                                    formData.name ||
                                                                    "business-partner"
                                                                }`,
                                                            )
                                                        }
                                                    >
                                                        <Download className="h-4 w-4 mr-2" />
                                                        Export errors
                                                    </Button>
                                                )}
                                            {coaUploadResult.warnings.length > 0 && (
                                                <ul className="mt-1 space-y-0.5 list-disc list-inside text-amber-600">
                                                    {coaUploadResult.warnings
                                                        .slice(0, 3)
                                                        .map((warn, i) => (
                                                            <li key={i} className="text-xs">
                                                                {warn}
                                                            </li>
                                                        ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Loading CoA mappings */}
                            {isLoadingCoA && (
                                <div className="flex items-center justify-center py-4">
                                    <Loader size="sm" />
                                    <span className="ml-2 text-sm text-muted-foreground">
                                        Loading mappings...
                                    </span>
                                </div>
                            )}

                            {/* CoA mappings table */}
                            {coaMappings.length > 0 && !isLoadingCoA && (
                                <div className="border rounded-lg overflow-hidden">
                                    <div className="max-h-48 overflow-y-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-muted sticky top-0">
                                                <tr>
                                                    <th className="px-3 py-2 text-left font-medium">
                                                        Source Account #
                                                    </th>
                                                    <th className="px-3 py-2 text-left font-medium">
                                                        Source Account Name
                                                    </th>
                                                    <th className="px-3 py-2 text-left font-medium">
                                                        Propcode Level
                                                    </th>
                                                    <th className="px-3 py-2 text-left font-medium">
                                                        Account #
                                                    </th>
                                                    <th className="px-3 py-2 text-left font-medium">
                                                        Account Name
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {coaMappings.slice(0, 50).map((mapping) => (
                                                    <tr
                                                        key={mapping.id}
                                                        className={cn(
                                                            "even:bg-muted/40",
                                                            mapping.isInForce
                                                                ? ""
                                                                : "text-muted-foreground",
                                                        )}
                                                    >
                                                        <td className="px-3 py-2 font-mono text-xs">
                                                            {mapping.thirdAccountCode}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {mapping.thirdAccountName}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <span
                                                                className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${
                                                                    mapping.level === "A"
                                                                        ? "bg-blue-100 text-blue-700"
                                                                        : mapping.level === "P"
                                                                          ? "bg-purple-100 text-purple-700"
                                                                          : "bg-amber-100 text-amber-700"
                                                                }`}
                                                                title={
                                                                    mapping.level === "A"
                                                                        ? "All"
                                                                        : mapping.level === "P"
                                                                          ? "Property"
                                                                          : "Company"
                                                                }
                                                            >
                                                                {mapping.level}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 font-mono text-xs">
                                                            {mapping.yardiAccountCode}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {mapping.yardiAccountName}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {coaMappings.length > 50 && (
                                        <div className="px-3 py-2 bg-muted text-xs text-muted-foreground text-center">
                                            Showing 50 of {coaMappings.length} mappings
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <DialogFooter className="gap-2 pt-4 sticky bottom-0 bg-background border-t mt-4 -mx-2 px-2 pb-0">
                            {creationSuccess ? (
                                // Success state - just show Done button
                                <Button
                                    type="button"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Done
                                </Button>
                            ) : (
                                // Normal create/edit state
                                <>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            // Allow closing if not in the middle of an atomic operation
                                            if (
                                                !createdServiceProviderId ||
                                                coaUploadResult?.success
                                            ) {
                                                setIsModalOpen(false);
                                                resetForm();
                                            }
                                        }}
                                        disabled={isSubmitting || isUploadingCoA}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={
                                            isSubmitting ||
                                            isUploadingCoA ||
                                            !coaFile ||
                                            formData.employeeIds.length === 0 ||
                                            !formData.name.trim()
                                        }
                                    >
                                        {isSubmitting || isUploadingCoA ? (
                                            <>
                                                <Loader size="sm" className="mr-2" />
                                                {isUploadingCoA ? "Uploading CoA..." : "Saving..."}
                                            </>
                                        ) : (
                                            "Add Business Partner"
                                        )}
                                    </Button>
                                </>
                            )}
                        </DialogFooter>
                    </form>
                </Modal>
            </>
        );
    };
};
